import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';

import { apiTrackImpression } from 'api';
import { Book, Review } from '../../types';
import { Cover } from '../cover';
import { ArrowLeftIcon } from '../../icons';
import { sanitizeHtml } from '../../lib';
import { isTeamReview } from '../../constants';

import { RateBook } from 'components/rate-book';
import { Reviews } from '../reviews';
import { ReviewForm } from '../review-form';
import { UserReviewForm } from '../user-review-form';
import { BookShelf } from '../book-shelf';
import { MainInfo } from './main-info';
import { SummaryMeta } from './summary-meta';
import { ReadingOrder } from './reading-order';

import styles from './summary.module.scss';

interface SummaryProps {
  book: Book;
  reviews: Review[];
  nextBooks?: Book[];
  readBefore?: Book[];
  readNext?: Book[];
  onBack: () => void;
  onEdit?: () => void;
  onBookmark: (id: number) => void;
  onBookSelect?: (book: Book) => void;
  onCategorySelect?: (category: string) => void;
}

export const Summary = ({
  book,
  reviews,
  nextBooks = [],
  readBefore = [],
  readNext = [],
  onBack,
  onEdit,
  onBookmark,
  onBookSelect,
  onCategorySelect,
}: SummaryProps) => {
  const [expandedReviews, setExpandedReviews] = useState<Set<number>>(
    new Set(),
  );

  useEffect(() => {
    apiTrackImpression(book.id).catch(() => {});
  }, [book.id]);

  const bookReviews = reviews.filter((r) => r.bookId === book.id);

  const { hash } = useLocation();
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const targetReviewId = Number(hash.match(/^#review-(\d+)$/)?.[1]) || null;
  const hasTargetReview = bookReviews.some((r) => r.id === targetReviewId);

  useEffect(() => {
    if (!targetReviewId || !hasTargetReview) {
      return;
    }

    setExpandedReviews((prev) => new Set(prev).add(targetReviewId));
    setHighlightedId(targetReviewId);

    const timer = setTimeout(() => {
      document
        .getElementById(`review-${targetReviewId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);

    return () => clearTimeout(timer);
  }, [targetReviewId, hasTargetReview]);
  const teamReviews = bookReviews.filter(isTeamReview);
  const readerReviews = bookReviews.filter((r) => !isTeamReview(r));
  const reviewCount = teamReviews.length;
  const hasReadingOrder =
    !!onBookSelect && readBefore.length + readNext.length > 0;

  const reviewAverage = reviewCount
    ? Math.round(
        (teamReviews.reduce((sum, r) => sum + r.rating, 0) / reviewCount) * 10,
      ) / 10
    : 0;

  const bookWithRating: Book = {
    ...book,
    rating: reviewAverage,
    reviewCount,
  };

  const toggleReview = (id: number) => {
    setExpandedReviews((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  return (
    <div className={styles.summary}>
      <button className={styles.back} onClick={onBack}>
        <ArrowLeftIcon size={16} /> Back
      </button>
      <div
        className={cn(styles.hero, hasReadingOrder && styles.withOrder)}
        style={{ background: `${book.coverColor}14` }}
      >
        <div className={styles.coverWrap}>
          <Cover book={book} size="lg" />
        </div>
        <div className={styles.headline}>
          <MainInfo
            book={bookWithRating}
            onBookmark={onBookmark}
            onEdit={onEdit}
          />
          <RateBook bookId={book.id} />
          {book.description && (
            <div className={styles.about}>
              <span className={styles.aboutLabel}>
                Synopsis · not our opinion
              </span>
              {book.descriptionHtml && /<[a-z]/i.test(book.descriptionHtml) ? (
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(book.descriptionHtml),
                  }}
                />
              ) : (
                <p className={styles.description}>{book.description}</p>
              )}
            </div>
          )}
          <SummaryMeta book={book} onCategorySelect={onCategorySelect} />
        </div>
        {hasReadingOrder && onBookSelect && (
          <div className={styles.orderColumn}>
            <ReadingOrder
              books={readBefore}
              direction="before"
              onBookSelect={onBookSelect}
            />
            <ReadingOrder
              books={readNext}
              direction="next"
              onBookSelect={onBookSelect}
            />
          </div>
        )}
      </div>
      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>
          Reviews{reviewCount > 0 ? ` (${reviewCount})` : ''}
          <span className={styles.aboutLabel}> · our opinion</span>
        </h4>
        {reviewCount === 0 ? (
          <p className={styles.empty}>No reviews yet.</p>
        ) : (
          <Reviews
            reviews={teamReviews}
            expandedReviews={expandedReviews}
            onToggleReview={toggleReview}
            highlightedId={highlightedId}
          />
        )}
        <ReviewForm bookId={book.id} />
      </section>
      {readerReviews.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>From our guests</h4>
          <Reviews
            reviews={readerReviews}
            expandedReviews={expandedReviews}
            onToggleReview={toggleReview}
            highlightedId={highlightedId}
            variant="reader"
          />
        </section>
      )}
      <section className={styles.section}>
        <UserReviewForm key={book.id} bookId={book.id} />
      </section>
      {nextBooks.length > 0 && onBookSelect && (
        <section className={styles.section}>
          <BookShelf
            title="More like this"
            books={nextBooks}
            cardType="full"
            layout="grid"
            maxRows={1}
            onBookSelect={onBookSelect}
            onBookmark={onBookmark}
          />
        </section>
      )}
    </div>
  );
};
